import { Close, KeyboardArrowDown } from '@mui/icons-material';
import { createTheme, MenuItem, Select, SvgIcon, ThemeProvider } from '@mui/material';
import React, { useState } from 'react';
import moment from 'moment';
import { useAccountStore } from '../../../components/Stores/accountStore';
import { useMaterialsStore } from '../../../components/Stores/MaterialsStore';
import ButtonDesigned from '../../../components/UI-Elements/Buttons/ButtonDesigned';
import { Material } from '../../../sp-types/materialType';
import { formatToPriceInDollars } from '../../../utils/FormatValue';
import { getUnitObj } from '../../../utils/MassUnits/massUnitsUtils';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { useCurrencyRates } from '@hooks/useCurrencyRates';
import { IInventoryReportType } from '../../../sp-types/InventoryReportType';
import { saveInventoryReport } from '../../../sp-firebase/inventoryReportFB';
import { Timestamp } from 'firebase/firestore';
import { MaterialTransaction } from './TransactionGrid/transactionGridUtils';
import { LoadingSpinnerOverlay } from '../../../components/UI-Elements/LoadingSpinnerOverlay';

const BlueArrowIcon = (props: any) => (
  <SvgIcon {...props} style={{ color: '#6571ff' }}>
    <KeyboardArrowDown />
  </SvgIcon>
);

const theme = createTheme({
  palette: { primary: { main: '#6571FF' } },
  components: { MuiTextField: { styleOverrides: { root: { '& .MuiInputBase-input': { padding: '5px 10px' } } } } },
});

interface Props {
  materials: Material[];
  cancel: () => void;
  onInvAdded: (_isUpdate: boolean) => void;
  isUpdate?: boolean;
  updateRow?: MaterialTransaction;
}
export const AddMaterialPopup: React.FC<Props> = ({ cancel, onInvAdded, isUpdate = false, updateRow }) => {
  const { materialsMap } = useMaterialsStore();
  const { pricesMap, currencies, getCurrencySymbol, getCurrencyInUSD } = useCurrencyRates();
  const { account } = useAccountStore();
  const [selectedMaterial, setSelectedMaterial] = useState<string>('');
  const [quantity, setQuantity] = useState<number>(0);
  const [suppliedQty, setSuppliedQty] = useState<number>(0);
  const [isRatio, setIsRatio] = useState<boolean>(false);
  const [ratio, setRatio] = useState<number>(0);
  const [actualPrice, setActualPrice] = useState<number>(0);
  const [date, setDate] = useState<string>(new Date().toISOString());
  const [price, setPrice] = useState<string>('');
  const [unit, setUnit] = useState<string>('');
  const [storage, setStorage] = React.useState<string>('PO');
  const [showDatePicker, setShowDatePicker] = React.useState<boolean>(true);
  const [poId, setPoId] = React.useState<string>('');
  const [poBatchId, setPoBatchId] = React.useState<string>('');
  const [isSkipActual, setIsSkipActual] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [currency, setCurrency] = React.useState<string>('USD');
  const [supplier, setSupplier] = React.useState<string>('');
  const [dontSum, setDontSum] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (selectedMaterial && materialsMap[selectedMaterial]) {
      const currPrice =
        (selectedMaterial && pricesMap[selectedMaterial] && formatToPriceInDollars(pricesMap[selectedMaterial], 1)) ||
        '';
      setPrice(currPrice);

      if (!isSkipActual) {
        setActualPrice(+pricesMap[selectedMaterial]?.toFixed(0) || 0);
      }
      setIsSkipActual(false);
      setUnit((selectedMaterial && materialsMap[selectedMaterial]?.unit) || '');
    }
  }, [selectedMaterial]);

  React.useEffect(() => {
    if (isUpdate && updateRow) {
      setSelectedMaterial(updateRow.materialId);
      setIsSkipActual(true);

      setQuantity(updateRow.quantity);
      setActualPrice(updateRow.actualPrice || 0);
      setDate(updateRow.reportingTime?.toDate().toLocaleDateString('en-CA') || '');
      setStorage(updateRow.isPO ? 'PO' : 'Storage');
      setShowDatePicker(!!updateRow.isPO);
      setSuppliedQty(updateRow.suppliedQty || 0);
      setPoId(updateRow.poId);
      setPoBatchId(updateRow.poBatchId);
      setCurrency(updateRow.currency || 'USD');
      setRatio(updateRow.ratio || 0);
      setIsRatio(!!updateRow.ratio);
      setSupplier(updateRow.supplier || '');
      setDontSum(updateRow.dontSum || false);
    }
  }, [updateRow]);

  const onStorageChange = (value: string) => {
    setStorage(value);
    setShowDatePicker(value !== 'Storage');
  };

  const addMaterialToInventory = async (isKeepOpen: boolean) => {
    const reportDate = new Date(storage === 'PO' ? date : moment().subtract(1, 'month').toISOString());

    const inventoryReport: IInventoryReportType = {
      ...updateRow,
      key: updateRow?.key || 2,
      accountId: account.id || '',
      materialId: selectedMaterial,
      quantity,
      suppliedQty,
      ratio: materialsMap[selectedMaterial]?.hasRatio && isRatio ? ratio : 0,
      reportingTime: Timestamp.fromDate(reportDate),
      monthYear: { month: reportDate.getMonth(), year: reportDate.getFullYear() },
      status: 'completed',
      isPO: storage === 'PO',
      poId,
      poBatchId,
      actualPrice,
      currency,
      supplier,
      dontSum,
    };

    setLoading(true);
    await saveInventoryReport(inventoryReport, !isUpdate);

    onInvAdded(isKeepOpen);
    setLoading(false);
  };

  return (
    <div className="relative bg-white flex flex-col p-4 gap-4 min-w-96">
      <div className="flex flex-row justify-between">
        <h4>{isUpdate ? 'Update inventory report' : 'Add inventory'}</h4>
        <Close className="cursor-pointer align-self-baseline text-purple me-1" onClick={cancel} />
      </div>
      <div className="gap-2 flex flex-col">
        <p className="text-sm text-gray-450">Material</p>
        <Select
          className="w-full h-8 rounded-md"
          IconComponent={BlueArrowIcon}
          value={selectedMaterial}
          placeholder="Select material"
          onChange={({ target }) => setSelectedMaterial(target.value)}
          displayEmpty
        >
          <MenuItem value="" disabled>
            Select material
          </MenuItem>
          {account.materialsToAccount.map((item, index) => (
            <MenuItem key={index} id={item.materialId} value={item.materialId}>
              {materialsMap[item.materialId] && materialsMap[item.materialId].name}
            </MenuItem>
          ))}
        </Select>
      </div>

      <div className="flex flex-row justify-between">
        <p className="text-sm text-gray-450">Price per {unit || 'ton'}:</p>
        <label className="text-lg mt-auto">{price ? price : ''}</label>
      </div>
      <div className="flex gap-2">
        <div className="flex flex-col gap-2">
          <p className="text-sm text-gray-450">Actual price</p>
          <input
            title="Actual price"
            type="number"
            disabled={!selectedMaterial}
            value={actualPrice}
            className="border-1 rounded-md border-gray-300 px-2 w-28 focus:outline-blue-600"
            onChange={({ target }) => setActualPrice(+target.value)}
          />
        </div>
        <div className="flex flex-col gap-2">
          <p className="text-sm text-gray-450">Currency</p>
          <Select
            className="h-[26px] rounded-md"
            IconComponent={BlueArrowIcon}
            value={currency}
            onChange={({ target }) => setCurrency(target.value)}
          >
            {currencies.map((curr) => (
              <MenuItem key={curr} value={curr}>
                {getCurrencySymbol(curr)}
              </MenuItem>
            ))}
          </Select>
        </div>
        {currency !== 'USD' && actualPrice > 0 && (
          <div className="flex flex-col gap-2 justify-end">
            <label className="text-sm text-gray-600">
              ≈ ${(actualPrice * getCurrencyInUSD(currency)).toFixed(2)} USD
            </label>
          </div>
        )}
      </div>
      {showDatePicker && (
        <div className="flex flex-col gap-2 ">
          <ThemeProvider theme={theme}>
            <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="en">
              <p className="text-sm text-gray-450">Date</p>
              <DatePicker
                format="DD/MM/YYYY"
                value={moment(date)}
                onChange={(date: moment.Moment | null) => setDate(date ? date.toISOString() : '')}
                slotProps={{ openPickerButton: { color: 'primary' } }}
              />
            </LocalizationProvider>
          </ThemeProvider>
        </div>
      )}
      <div className="flex gap-2 justify-between">
        <div className="flex flex-col gap-2 justify-between">
          <p className="text-sm text-gray-450">Quantity</p>
          <div className="flex flex-row flex-1 gap-2">
            <input
              title="Input Quantity"
              type="number"
              value={quantity}
              className="border-1 rounded-md border-gray-300 px-2 w-28 focus:outline-blue-600"
              onChange={({ target }) => setQuantity(Number(target.value))}
            />
            <p className="text-sm content-center">{getUnitObj(unit).shortForm}</p>
          </div>
        </div>
        <div className="flex flex-col gap-2 justify-between">
          <p className="text-sm text-gray-450">Supplied Qty</p>
          <div className="flex flex-row flex-1 gap-2">
            <input
              title="Supplied Qty"
              type="number"
              value={suppliedQty}
              className="border-1 rounded-md border-gray-300 px-2 w-28 focus:outline-blue-600"
              onChange={({ target }) => setSuppliedQty(Number(target.value))}
            />
            <p className="text-sm content-center">{getUnitObj(unit).shortForm}</p>
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-2 justify-between">
        <p className="text-sm text-gray-450">Storage or PO?</p>
        <div className="flex flex-row flex-1 gap-2">
          <Select
            className="w-full h-8 rounded-md"
            IconComponent={BlueArrowIcon}
            value={storage}
            placeholder="Storage"
            onChange={({ target }) => onStorageChange(target.value)}
            displayEmpty
          >
            <MenuItem value="Storage">Storage</MenuItem>
            <MenuItem value="PO">PO</MenuItem>
          </Select>
        </div>
      </div>
      {storage === 'PO' && (
        <>
          <div className="flex gap-2 justify-between">
            <div className="flex flex-col gap-2 justify-between">
              <p className="text-sm text-gray-450">PO ID</p>
              <div className="flex flex-row flex-1 gap-2">
                <input
                  title="PO ID"
                  type="text"
                  value={poId}
                  className="border-1 rounded-md border-gray-300 px-2 w-32 focus:outline-blue-600"
                  onChange={({ target }) => setPoId(target.value)}
                />
              </div>
            </div>
            <div className="flex flex-col gap-2 justify-between">
              <p className="text-sm text-gray-450">Batch #</p>
              <div className="flex flex-row flex-1 gap-2">
                <input
                  title="Batch #"
                  type="number"
                  value={poBatchId}
                  className="border-1 rounded-md border-gray-300 px-2 w-32 focus:outline-blue-600"
                  onChange={({ target }) => setPoBatchId(target.value)}
                />
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <p className="text-sm text-gray-450">Supplier</p>
            <input
              title="Supplier"
              type="text"
              value={supplier}
              placeholder="Enter supplier name"
              className="border-1 rounded-md border-gray-300 px-2 h-8 focus:outline-blue-600"
              onChange={({ target }) => setSupplier(target.value)}
            />
          </div>
        </>
      )}
      {selectedMaterial && materialsMap[selectedMaterial].hasRatio && (
        <div className="flex gap-5">
          <div className="flex flex-col gap-2">
            <p className="text-sm text-gray-450">Report Ratio?</p>
            <input
              type="checkbox"
              checked={isRatio}
              onChange={({ target }) => setIsRatio(target.checked)}
              className="cursor-pointer"
            />
          </div>
          <div className="flex flex-col gap-2">
            <p className="text-sm text-gray-450">Ratio</p>
            <input
              title="Input Ratio"
              type="number"
              disabled={!isRatio}
              value={ratio}
              className="border-1 rounded-md border-gray-300 px-2 w-28 focus:outline-blue-600"
              onChange={({ target }) => setRatio(+target.value)}
            />
          </div>
        </div>
      )}

      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="dontSum"
            checked={dontSum}
            onChange={({ target }) => setDontSum(target.checked)}
            className="cursor-pointer"
          />
          <label htmlFor="dontSum" className="text-sm text-gray-450 cursor-pointer">
            Dont include in dashboard calculations
          </label>
        </div>
      </div>

      <div className="flex flex-row justify-between gap-4">
        <ButtonDesigned type="button" onButtonClick={cancel} style="outline" className="flex-1">
          Cancel
        </ButtonDesigned>
        <ButtonDesigned
          disableButton={!selectedMaterial || !quantity}
          type="button"
          onButtonClick={() => addMaterialToInventory(true)}
          className="flex-1"
        >
          {isUpdate ? 'Save and keep open' : 'Add and keep open'}
        </ButtonDesigned>
        <ButtonDesigned
          disableButton={!selectedMaterial || !quantity}
          type="button"
          onButtonClick={() => addMaterialToInventory(false)}
          className="flex-1"
        >
          {isUpdate ? 'Save' : 'Add'}
        </ButtonDesigned>
      </div>

      {loading && <LoadingSpinnerOverlay size={64} />}
    </div>
  );
};
